import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { clientService } from "../../services/client.service";
import { Search, Plus, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import CreateClientModal from "./CreateClientModal";

function useConditionalBodyScroll(enabled) {
  useEffect(() => {
    if (enabled) {
      document.body.style.overflow = "auto";
    } else {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [enabled]);
}

export default function ClientPicker({ onSelect, onClose }) {
  const [recentClients, setRecentClients] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const searchInputRef = useRef(null);

  /* ───────── Active list (single mental model) ───────── */
  const list = useMemo(
    () => (query ? results : recentClients),
    [query, results, recentClients]
  );

  const isSmallViewport =
    typeof window !== "undefined" &&
    window.innerHeight < 360;

  useConditionalBodyScroll(isSmallViewport);

  /* ───────── Autofocus search input on mount ───────── */
  useEffect(() => {
    // Small delay to ensure portal is mounted
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  /* ───────── Load recent on open ───────── */
  useEffect(() => {
    loadRecent();
  }, []);


  async function loadRecent() {
    setLoading(true);
    const data = await clientService.picker({ recent: true });
    setRecentClients(data);
    setLoading(false);
  }

  /* ───────── Search (debounced) ───────── */
  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }

    const t = setTimeout(async () => {
      setLoading(true);
      const data = await clientService.picker({ search: query });
      setResults(data);
      setLoading(false);
    }, 250);

    return () => clearTimeout(t);
  }, [query]);

  /* ───────── Reset selection on list change ───────── */
  useEffect(() => {
    setActiveIndex(0);
  }, [list]);

  /* ───────── Keyboard navigation ───────── */
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, list.length - 1));
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      }

      if (e.key === "Enter" && list[activeIndex]) {
        e.preventDefault();
        onSelect(list[activeIndex]);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [list, activeIndex, onClose, onSelect]);

  // Render using Portal to escape parent constraints
  // Hide picker when create modal is open
  if (showCreateModal) {
    return createPortal(
      <CreateClientModal
        onClose={() => {
          setShowCreateModal(false);
          onClose();
        }}
        onSave={(client) => {
          setShowCreateModal(false);
          onSelect(client);
        }}
      />,
      document.body
    );
  }

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[1000] flex items-start justify-center pt-[20vh]">
        {/* Backdrop with blur */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          onWheel={(e) => e.stopPropagation()}
        />

        {/* Picker Container - Command Palette Style */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{
            duration: 0.2,
            ease: [0.16, 1, 0.3, 1], // Ease-out curve
          }}
          className="relative w-[560px] max-w-[92vw] h-[420px] rounded-2xl bg-white border border-slate-200 shadow-[0_24px_80px_rgba(0,0,0,0.25)] overflow-hidden z-[1001]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex flex-col w-full h-full">
            {/* Search Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-gradient-to-b from-slate-50/50 to-transparent">
              <Search size={18} className="text-slate-400 flex-shrink-0" />
              <input
                ref={searchInputRef}
                placeholder="Search clients by name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex-1 text-sm outline-none bg-transparent placeholder:text-slate-400 text-slate-900"
              />
              <button
                onClick={onClose}
                className="flex items-center justify-center w-6 h-6 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Results List */}
            <div className="flex-1 overflow-y-auto px-2 py-2">
              {loading && list.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="inline-block w-5 h-5 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
                  <div className="text-sm text-slate-500 mt-3">Loading clients...</div>
                </div>
              ) : list.length > 0 ? (
                list.map((client, i) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    active={i === activeIndex}
                    onClick={() => onSelect(client)}
                  />
                ))
              ) : query ? (
                <EmptyState query={query} onCreate={() => setShowCreateModal(true)} />
              ) : (
                <div className="py-12 text-center">
                  <div className="text-sm text-slate-500">No recent clients</div>
                  <div className="text-xs text-slate-400 mt-1">Create your first client below</div>
                </div>
              )}
            </div>

            {/* Footer Action */}
            {!query && (
              <div className="px-4 py-3 border-t border-slate-200 bg-gradient-to-t from-slate-50/50 to-transparent">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-4 py-2.5 rounded-lg transition-all duration-150 flex items-center justify-center gap-2"
                >
                  <Plus size={16} />
                  Create new client
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}

/* ───────── EMPTY STATE ───────── */

function EmptyState({ query, onCreate }) {
  return (
    <div className="py-12 text-center">
      <div className="text-sm font-medium text-slate-700">
        No client found for "{query}"
      </div>
      <div className="text-xs text-slate-500 mt-1 mb-4">
        Create a new client with this name
      </div>
      <button
        onClick={onCreate}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
      >
        <Plus size={14} />
        Create "{query}"
      </button>
    </div>
  );
}

/* ───────── COMMAND ROW ───────── */

function ClientRow({ client, active, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`
        group flex items-center justify-between
        px-4 py-3 rounded-lg cursor-pointer
        transition-all duration-150
        ${active
          ? "bg-indigo-50 ring-2 ring-indigo-100"
          : "hover:bg-slate-50"}
      `}
    >
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-semibold truncate transition-colors ${
          active ? "text-indigo-900" : "text-slate-900 group-hover:text-slate-900"
        }`}>
          {client.name}
        </div>
        <div className={`text-xs truncate mt-0.5 transition-colors ${
          active ? "text-indigo-700" : "text-slate-500 group-hover:text-slate-600"
        }`}>
          {client.city}
          {client.state && `, ${client.state}`}
        </div>
      </div>

      {client.gstin && (
        <span className={`
          ml-3 shrink-0
          text-[10px] font-bold px-2 py-1 rounded-md
          transition-colors
          ${active 
            ? "bg-emerald-100 text-emerald-700" 
            : "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100"}
        `}>
          GST
        </span>
      )}
    </div>
  );
}
